import React, { useState, useEffect, useMemo } from 'react';
import { SURVEY_SECTIONS } from '../data/surveySections';
import './SurveySection.css';
import { useAuth } from '../context/AuthContext';
import { saveResponses } from '../services/SurveyService';
import { createTempAnonSurvey } from '../services/TempService';
import { loginUser, register as registerUser } from '../services/UserService';
import { getUserFacingErrorMessage } from '../services/errors';
import {
  PASSWORD_REQUIREMENTS_TEXT,
  validateEmail,
  validatePasswordStrength,
} from '../utils/validation';
import { useNavigate } from 'react-router-dom';
import { refreshDashboardCache } from '../services/CacheService';

// Likert scale (displayed left to right: 1 -> 5)
const LIKERT_SCALE = [
  { value: 1, label: '' },
  { value: 2, label: '' },
  { value: 3, label: '' },
  { value: 4, label: '' },
  { value: 5, label: '' },
];

export default function SurveySection({ compact = false }) {
  const { token, login: loginAuth, userName, userEmail } = useAuth();
  const navigate = useNavigate();
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [attemptedNext, setAttemptedNext] = useState(false);
  const [activeQuestionId, setActiveQuestionId] = useState(null); // track hover/focus
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 640 : false,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showProcessingLocal, setShowProcessingLocal] = useState(false);
  const [startTime] = useState(new Date());

  // Auth modal state for post-survey submission
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login'); // 'login' | 'register'
  const [pendingPayload, setPendingPayload] = useState(null);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [registerForm, setRegisterForm] = useState({
    email: '',
    name: '',
    password: '',
  });
  const [registerError, setRegisterError] = useState('');
  const [registerSuccess, setRegisterSuccess] = useState('');
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);

  const setCookie = (name, value, hours) => {
    let expires = '';
    if (hours) {
      const date = new Date();
      date.setTime(date.getTime() + hours * 60 * 60 * 1000);
      expires = '; expires=' + date.toUTCString();
    }
    document.cookie = name + '=' + (value || '') + expires + '; path=/';
  };

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Group first 36 scenario sections into batches of 3; final personal info section is last
  const personalInfoSection = SURVEY_SECTIONS[SURVEY_SECTIONS.length - 1];
  const scenarioSections = SURVEY_SECTIONS.slice(0, SURVEY_SECTIONS.length - 1); // 36
  const groupedScenarioSections = useMemo(() => {
    const grouped = [];
    for (let i = 0; i < scenarioSections.length; i += 3) {
      grouped.push(scenarioSections.slice(i, i + 3));
    }
    return grouped; // 12 groups
  }, [scenarioSections]);

  // Do not include the personal info step for anonymous submissions.
  // Previously anonymous users were asked for their name in the final step.
  // New behavior: the survey does not collect personal name at the end for
  // anonymous users and we omit that step entirely. Logged-in users also
  // don't see a personal-info step because their profile is used when needed.
  const steps = useMemo(
    () => groupedScenarioSections,
    [groupedScenarioSections],
  );
  // total 13 steps when unauthenticated

  const totalSteps = steps.length;
  const currentStepNumber = stepIndex + 1;
  const currentGroup = steps[stepIndex];

  // Determine whether all required questions on the current group are answered.
  // For the personal info section when the user is anonymous, only q1 (name)
  // is required.
  const allAnswered = currentGroup.every((sec) => {
    const requiredQuestions =
      sec === personalInfoSection && !token
        ? sec.questions.filter((q) => q.id === 'q1')
        : sec.questions;
    return requiredQuestions.every((q) => answers[q.id]);
  });

  const setAnswer = (id, val) => setAnswers((a) => ({ ...a, [id]: val }));

  // Test helper: prefill answers and jump to final step
  const fillTestData = () => {
    const newAns = {};
    // Prefill likert responses for 36 situations with random selections (1-5)
    scenarioSections.forEach((sec) => {
      sec.questions.forEach((q) => {
        // Randomly pick a Likert value between 1 and 5 (inclusive)
        newAns[q.id] = Math.floor(Math.random() * LIKERT_SCALE.length) + 1;
      });
    });
    // Prefill personal info only for logged-in users. Anonymous users no
    // longer need to provide a name at the end of the survey.
    if (token) {
      newAns['q1'] = 'Tianren Zhang';
      // When logged in also fill age/email for convenience
      newAns['q2'] = '24';
      newAns['q3'] = 'tianren30@gmail.com';
    }

    setAnswers(newAns);
    setStepIndex(steps.length - 1);
    console.debug('[Survey] Test data filled. Jumped to last step.');
    const el = document.getElementById('survey-section');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const formatDateTime = (d) => {
    const datePart = new Date(d).toLocaleDateString('en-US');
    const timePart = new Date(d).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
    return `${datePart} ${timePart}`;
  };

  const likertToLabel = (v) => {
    switch (v) {
      case 1:
        return 'Highly unlikely';
      case 2:
        return 'Unlikely';
      case 3:
        return 'Neutral/Uncertain';
      case 4:
        return 'Likely';
      case 5:
        return 'Highly likely';
      default:
        return '';
    }
  };

  const buildPayload = () => {
    // Map scenario answers to VxRy keys
    const responses = {};
    scenarioSections.forEach((sec, i) => {
      sec.questions.forEach((q, j) => {
        const key = `V${i + 1}R${j + 1}`;
        const raw = answers[q.id];
        responses[key] = likertToLabel(Number(raw));
      });
    });

    // For logged-in users prefer the cached profile values; anonymous users
    // will provide name (q1) and optionally age/email in answers.
    // Only include name when the user is authenticated. Anonymous
    // submissions intentionally omit personal name data.
    const name = token ? (answers['q1'] || userName || '').trim() : undefined;
    const ageRaw = (answers['q2'] || '').toString().trim();
    const email = (answers['q3'] || userEmail || '').trim();
    const parsedAge = Number(ageRaw);
    const age = token
      ? undefined
      : ageRaw === '' || !Number.isFinite(parsedAge)
        ? undefined
        : parsedAge;

    const payload = {
      startTime: formatDateTime(startTime),
      completionTime: formatDateTime(new Date()),
      responses,
    };

    // Only attach name when present and the user is authenticated.
    if (token && name) payload.name = name;

    // Only include email when available (either provided or cached)
    if (email) payload.email = email;
    // Include age only for anonymous submissions (per new flow)
    if (typeof age !== 'undefined') payload.age = age;

    return payload;
  };

  const scrollFirstUnanswered = () => {
    for (const sec of currentGroup) {
      const questionsToCheck =
        sec === personalInfoSection && !token
          ? sec.questions.filter((q) => q.id === 'q1')
          : sec.questions;
      const firstUnanswered = questionsToCheck.find((q) => !answers[q.id]);
      if (firstUnanswered) {
        console.debug(
          '[Survey] First unanswered question id:',
          firstUnanswered.id,
          'in section',
          sec.sectionNumber,
        );
        const el = document.getElementById(`question-${firstUnanswered.id}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        break;
      }
    }
  };

  const handleNext = async () => {
    if (!allAnswered) {
      setAttemptedNext(true);
      scrollFirstUnanswered();
      return;
    }
    setAttemptedNext(false);
    if (stepIndex < steps.length - 1) {
      console.debug('[Survey] Advancing step', stepIndex, '->', stepIndex + 1);
      setStepIndex((i) => i + 1);
      const el = document.getElementById('survey-section');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Final step: submit
      try {
        const payload = buildPayload();
        if (!token) {
          // Unauthenticated flow: submit an anonymous TEMP survey to public endpoint
          try {
            setIsSubmitting(true);
            // Request body is unchanged vs previous anon call.
            const anonPayload = {
              startTime: payload.startTime,
              completionTime: payload.completionTime,
              name: payload.name,
              responses: payload.responses,
            };
            const resp = await createTempAnonSurvey(anonPayload);

            // Cache temp survey result locally for the anonymous flow.
            const tempObj = {
              tempId: resp?.tempId,
              briefResult: resp?.briefResult,
              rBriefResult: resp?.rBriefResult ?? null,
              expiresAt: resp?.expiresAt,
            };
            try {
              localStorage.setItem('anonTempSurvey', JSON.stringify(tempObj));
            } catch (e) {
              console.warn(
                'Failed to persist anon temp survey to localStorage',
                e,
              );
            }

            const result = {
              // As before, the result page reads a markdown-ish brief string.
              // For the new API, we display rBriefResult when present.
              briefResultText: tempObj.rBriefResult || tempObj.briefResult,
              tempId: tempObj.tempId,
              expiresAt: tempObj.expiresAt,
            };

            navigate('/survey-result', { state: { result } });
            return;
          } catch (err) {
            console.error('Anonymous survey submission failed', err);
            alert('Failed to submit anonymous survey. Please try again.');
            return;
          } finally {
            setIsSubmitting(false);
          }
        }
        setIsSubmitting(true);
        // show local processing overlay and signal SurveyResultPage if mounted
        setShowProcessingLocal(true);
        try {
          window.dispatchEvent(new CustomEvent('surveyProcessingStart'));
        } catch (ev) {}
        // allow the browser to render the overlay
        await new Promise((res) => requestAnimationFrame(res));
        console.debug('[Survey] Token present:', Boolean(token));
        console.debug(
          '[Survey] Responses count:',
          Object.keys(payload.responses || {}).length,
        );
        const result = await saveResponses(payload, token);
        // Submission succeeded; avoid logging server response or collected answers in production
        try {
          await refreshDashboardCache(token);
        } catch {}
        navigate('/survey-result', { state: { result } });
        try {
          window.dispatchEvent(new CustomEvent('surveyProcessingEnd'));
        } catch (ev) {}
        return;
      } catch (e) {
        console.error('Failed to submit survey responses', e);
        alert('Failed to submit responses. Please try again.');
      } finally {
        setIsSubmitting(false);
        setShowProcessingLocal(false);
        try {
          window.dispatchEvent(new CustomEvent('surveyProcessingEnd'));
        } catch (ev) {}
      }
    }
  };

  // Auth modal handlers
  const submitLogin = async (e) => {
    e?.preventDefault?.();
    setLoginError('');
    if (!pendingPayload) {
      setLoginError('No survey payload to submit.');
      return;
    }
    try {
      const data = await loginUser(loginEmail, loginPassword);
      const newToken = data.token;
      if (!newToken) throw new Error('No token returned');
      setCookie('token', newToken, 1);
      loginAuth(newToken);
      setIsSubmitting(true);
      const result = await saveResponses(pendingPayload, newToken);
      try {
        await refreshDashboardCache(newToken);
      } catch {}
      // Submission succeeded; do not log deferred payload or server response
      setShowAuthModal(false);
      setPendingPayload(null);
      navigate('/survey-result', { state: { result } });
      return;
    } catch (err) {
      console.error('Login failed in modal', err);
      setLoginError('Invalid email or password');
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitRegister = async (e) => {
    e?.preventDefault?.();
    setRegisterError('');
    setRegisterSuccess('');
    try {
      setIsSubmitting(true);
      const emailCheck = validateEmail(registerForm.email);
      if (!emailCheck.ok) {
        setRegisterError(emailCheck.message);
        return;
      }

      const pwCheck = validatePasswordStrength(registerForm.password);
      if (!pwCheck.ok) {
        setRegisterError(pwCheck.message);
        return;
      }

      const data = await registerUser({
        ...registerForm,
        email: emailCheck.normalized,
      });
      const newToken = data?.token;
      if (!newToken) {
        // Fallback: if no token, keep previous flow
        setRegisterSuccess(
          'Registration successful. Please log in to submit your responses.',
        );
        setAuthMode('login');
        setLoginEmail(registerForm.email);
        return;
      }
      // Auto-login and submit
      setCookie('token', newToken, 1);
      loginAuth(newToken);
      if (pendingPayload) {
        const result = await saveResponses(pendingPayload, newToken);
        try {
          await refreshDashboardCache(newToken);
        } catch {}
        setPendingPayload(null);
        setShowAuthModal(false);
        navigate('/survey-result', { state: { result } });
        return;
      } else {
        console.debug('[Survey] No pending payload found after registration.');
      }
      setShowAuthModal(false);
    } catch (err) {
      console.error('Registration failed in modal', err);
      setRegisterError(getUserFacingErrorMessage(err, 'register'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const getMotivationMessage = (currentStep, totalSteps) => {
    if (currentStep === totalSteps)
      return 'Final step — thanks for sticking with it!';
    if (currentStep === totalSteps - 1)
      return 'Almost there — just one more step after this.';
    if (currentStep >= Math.ceil(totalSteps / 2))
      return "You're over halfway — keep going!";
    if (currentStep > 1)
      return 'Great progress — your answers are really helpful.';
    return 'Take your time and answer as honestly as you can.';
  };

  const handlePrev = () => {
    if (stepIndex > 0) {
      setStepIndex((i) => i - 1);
      setAttemptedNext(false);
      const el = document.getElementById('survey-section');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const processingActive =
    showProcessingLocal || (isSubmitting && stepIndex === steps.length - 1);

  return (
    <section
      id='survey-section'
      className={`survey-section ${processingActive ? 'blurred' : ''}`}
    >
      {processingActive && (
        <div className='processing-overlay' role='status' aria-live='polite'>
          <div className='processing-card'>
            <div className='spinner' aria-hidden />
            <div className='processing-text'>
              Preparing your brief report, please wait.
            </div>
          </div>
        </div>
      )}
      <div className='survey-inner'>
        {/* Intro / time estimate */}
        <div className='survey-intro'>
          <p className='survey-intro-main'>
            The Innerscience Personality Report (IPR) test takes approximately{' '}
            <strong>10-15 minutes</strong> to complete.
          </p>

          <p className='survey-intro-sub'>
            There are <strong>{totalSteps}</strong> steps in total. You can move
            back to review your answers at any time before you submit.
          </p>
        </div>

        {/* Top step indicator with extra bottom spacing */}
        <div
          className='step-indicator-wrap top'
          style={{
            marginTop: compact ? (isMobile ? 4 : 8) : isMobile ? 8 : 16,
            marginBottom: compact ? (isMobile ? 10 : 14) : isMobile ? 18 : 28,
          }}
        >
          {steps.map((_, idx) => (
            <div
              key={idx}
              className={`step-dot ${idx === stepIndex ? 'active' : ''}`}
            ></div>
          ))}
        </div>

        {/* Numeric progress + progress bar */}
        <div className='survey-progress-summary'>
          <div className='survey-progress-text'>
            {currentStepNumber === totalSteps ? (
              <span>
                Final step ({currentStepNumber} of {totalSteps})
              </span>
            ) : (
              <span>
                Step {currentStepNumber} of {totalSteps}
              </span>
            )}
          </div>
          <div className='survey-progress-bar'>
            <div
              className='survey-progress-bar-fill'
              style={{ width: `${(currentStepNumber / totalSteps) * 100}%` }}
            ></div>
          </div>
          <div className='survey-motivation'>
            {getMotivationMessage(currentStepNumber, totalSteps)}
          </div>
        </div>
        {currentGroup.map((sec, idx) => {
          const isLastInGroup = idx === currentGroup.length - 1;
          const groupPositionClass =
            idx === 0
              ? 'section-group--top'
              : idx === 1
                ? 'section-group--middle'
                : 'section-group--bottom';
          // Determine which questions to render for this section.
          // When rendering the personal info section for anonymous users,
          // only show the name field (q1). If user is logged in the whole
          // personal info step is omitted via steps above.
          const questionsToRender =
            sec === personalInfoSection && !token
              ? sec.questions.filter((q) => q.id === 'q1')
              : sec.questions;

          return (
            <div
              key={sec.sectionNumber}
              className={`section-group ${groupPositionClass}`}
              style={{
                marginBottom: isLastInGroup ? 0 : undefined,
                paddingBottom: isLastInGroup ? 0 : undefined,
              }}
            >
              {sec.title && !/^\[Situation \d+\]/.test(sec.title) && (
                <h2 className='section-header'>{sec.title}</h2>
              )}
              {sec.description && (
                <p className='section-description'>
                  <strong>Section {sec.sectionNumber}:</strong>{' '}
                  {sec.description}
                </p>
              )}
              {questionsToRender.map((q) => {
                const unanswered = attemptedNext && !answers[q.id];
                const answered = !!answers[q.id];
                const isActive = activeQuestionId === q.id;

                return (
                  <div
                    key={q.id}
                    id={`question-${q.id}`}
                    onMouseEnter={() => setActiveQuestionId(q.id)}
                    onMouseLeave={() => {
                      if (activeQuestionId === q.id) setActiveQuestionId(null);
                    }}
                    className={`question-row ${answered ? 'answered' : ''} ${
                      isActive ? 'active' : ''
                    } ${unanswered ? 'invalid' : ''}`}
                  >
                    <div className='question-text'>
                      {q.text.replace(/^\[Response \d+\]\s*/, '')}
                    </div>
                    {q.type === 'text' && (
                      <div className='input-wrap'>
                        <input
                          type='text'
                          value={answers[q.id] || ''}
                          onFocus={() => setActiveQuestionId(q.id)}
                          onBlur={() => setActiveQuestionId(null)}
                          onChange={(e) =>
                            setAnswer(
                              q.id,
                              e.target.value.trim() === ''
                                ? ''
                                : e.target.value,
                            )
                          }
                          placeholder='Enter your answer'
                        />
                      </div>
                    )}
                    {q.type === 'likert' && (
                      <div className='scale-row-outer'>
                        <div className='scale-row'>
                          <span className='end-label'>HIGHLY UNLIKELY</span>
                          <div className='circles-wrap'>
                            {LIKERT_SCALE.map((pt) => {
                              const selected = answers[q.id] === pt.value;
                              return (
                                <div
                                  key={pt.value}
                                  onClick={() => setAnswer(q.id, pt.value)}
                                  onFocus={() => setActiveQuestionId(q.id)}
                                  onBlur={(e) => {
                                    if (
                                      !e.currentTarget.parentElement.contains(
                                        document.activeElement,
                                      )
                                    ) {
                                      setActiveQuestionId(null);
                                    }
                                  }}
                                  className={`likert-circle ${
                                    selected ? 'selected' : ''
                                  }`}
                                  role='radio'
                                  aria-checked={selected}
                                  tabIndex={0}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      setAnswer(q.id, pt.value);
                                    }
                                  }}
                                ></div>
                              );
                            })}
                          </div>
                          <span className='end-label'>HIGHLY LIKELY</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
        {attemptedNext && !allAnswered && (
          <div className='error-bar'>
            <span className='warning-icon'>⚠</span>
            <span>
              Oops, you missed something! Please{' '}
              <span className='go-back-link' onClick={scrollFirstUnanswered}>
                go back
              </span>{' '}
              and answer all the questions on the page.
            </span>
          </div>
        )}
        <div className='step-indicator-wrap bottom'>
          {steps.map((_, idx) => (
            <div
              key={idx}
              className={`step-dot ${idx === stepIndex ? 'active' : ''}`}
            ></div>
          ))}
        </div>

        {/* Numeric progress + progress bar */}
        <div className='survey-progress-summary'>
          <div className='survey-progress-text'>
            {currentStepNumber === totalSteps ? (
              <span>
                Final step ({currentStepNumber} of {totalSteps})
              </span>
            ) : (
              <span>
                Step {currentStepNumber} of {totalSteps}
              </span>
            )}
          </div>
          <div className='survey-progress-bar'>
            <div
              className='survey-progress-bar-fill'
              style={{ width: `${(currentStepNumber / totalSteps) * 100}%` }}
            ></div>
          </div>
          <div className='survey-motivation'>
            {getMotivationMessage(currentStepNumber, totalSteps)}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            className='btn-next'
            onClick={handleNext}
            disabled={isSubmitting}
          >
            {stepIndex === steps.length - 1
              ? isSubmitting
                ? 'SUBMITTING…'
                : 'FINISH >'
              : 'NEXT STEP >'}
          </button>
          <button type='button' onClick={fillTestData} disabled={isSubmitting} style={{display: 'none'}}>
            Test: Prefill Survey
          </button>
        </div>
        {stepIndex > 0 && (
          <div className='link-prev' onClick={handlePrev}>
            &lt; PREVIOUS STEP
          </div>
        )}
      </div>

      {/* Auth modal for unauthenticated submission */}
      {!token && showAuthModal && (
        <div className='modal-backdrop'>
          <div className='modal'>
            <h3>Sign in to submit your results</h3>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <button
                className={`btn ${
                  authMode === 'login' ? 'btn--primary' : 'btn--outline'
                }`}
                onClick={() => setAuthMode('login')}
                disabled={authMode === 'login'}
              >
                I have an account
              </button>
              <button
                className={`btn ${
                  authMode === 'register' ? 'btn--primary' : 'btn--outline'
                }`}
                onClick={() => setAuthMode('register')}
                disabled={authMode === 'register'}
              >
                I need to register
              </button>
            </div>

            {authMode === 'login' ? (
              <form onSubmit={submitLogin} className='form'>
                <input
                  className='input'
                  type='email'
                  placeholder='Email'
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  required
                />
                <div style={{ position: 'relative' }}>
                  <input
                    className='input'
                    type={showLoginPassword ? 'text' : 'password'}
                    placeholder='Password'
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    style={{ paddingRight: 44 }}
                    required
                  />
                  <button
                    type='button'
                    aria-label={
                      showLoginPassword ? 'Hide password' : 'Show password'
                    }
                    onClick={() => setShowLoginPassword((v) => !v)}
                    style={{
                      position: 'absolute',
                      right: 10,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      padding: 6,
                      lineHeight: 1,
                      color: 'inherit',
                      opacity: 0.85,
                    }}
                  >
                    {showLoginPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
                {loginError && <p className='form-error'>{loginError}</p>}
                <div className='modal__actions'>
                  <button
                    className='btn btn--primary'
                    type='submit'
                    disabled={isSubmitting}
                  >
                    Login & Submit
                  </button>
                  <button
                    className='btn btn--outline'
                    type='button'
                    onClick={() => setShowAuthModal(false)}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={submitRegister} className='form'>
                <input
                  className='input'
                  type='email'
                  placeholder='Email'
                  value={registerForm.email}
                  onChange={(e) =>
                    setRegisterForm({ ...registerForm, email: e.target.value })
                  }
                  required
                />
                <input
                  className='input'
                  type='text'
                  placeholder='Name'
                  value={registerForm.name}
                  onChange={(e) =>
                    setRegisterForm({ ...registerForm, name: e.target.value })
                  }
                  required
                />
                <div style={{ position: 'relative' }}>
                  <input
                    className='input'
                    type={showRegisterPassword ? 'text' : 'password'}
                    placeholder='Password'
                    value={registerForm.password}
                    onChange={(e) =>
                      setRegisterForm({
                        ...registerForm,
                        password: e.target.value,
                      })
                    }
                    style={{ paddingRight: 44 }}
                    required
                  />
                  <button
                    type='button'
                    aria-label={
                      showRegisterPassword ? 'Hide password' : 'Show password'
                    }
                    onClick={() => setShowRegisterPassword((v) => !v)}
                    style={{
                      position: 'absolute',
                      right: 10,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      padding: 6,
                      lineHeight: 1,
                      color: 'inherit',
                      opacity: 0.85,
                    }}
                  >
                    {showRegisterPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
                {registerError !== PASSWORD_REQUIREMENTS_TEXT && (
                  <p className='form-disclaimer'>
                    {PASSWORD_REQUIREMENTS_TEXT}
                  </p>
                )}
                {registerError && <p className='form-error'>{registerError}</p>}
                {registerSuccess && (
                  <p className='form-success'>{registerSuccess}</p>
                )}
                <div className='modal__actions'>
                  <button
                    className='btn btn--primary'
                    type='submit'
                    disabled={isSubmitting}
                  >
                    Register
                  </button>
                  <button
                    className='btn btn--outline'
                    type='button'
                    onClick={() => setShowAuthModal(false)}
                  >
                    Close
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
